import React, { useCallback, useEffect, useMemo, useState } from "react";
import PostCard from "../components/PostCard";
import CategoryPills from "../components/Categorypills";
import { readBookmarkedPosts } from "../bookmarks";
import { getAllTourBookmarkPackages, TourMiddleCard } from "./TourPackagesPage";
import { MemoizedDealCard } from "../components/DealCard";

const STORAGE = {
  TOUR_BOOKMARKS: "trw-tour-package-bookmarks-v1",
};

function readJsonArray(key, fallback = []) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : fallback;
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function writeJsonArray(key, value) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new Event("storage"));
}

function SectionH2({ children, id }) {
  return (
    <h2
      id={id}
      className="mt-0 mb-6 scroll-mt-[95px] border-l-[4px] border-[#d02525] py-0 pr-0 pl-[15px] [font-family:Poppins,sans-serif] text-[32px] leading-none font-medium text-black md:text-[36px]"
    >
      {children}
    </h2>
  );
}

function TagPill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-[12px] border px-[10px] py-[8px] text-[14px] font-[400] leading-[20px] transition-colors sm:px-4 sm:py-[11px] ${
        active
          ? "border-[#ff1f1f] text-[#ff1f1f]"
          : "border-black text-black hover:border-[#ff1f1f] hover:text-[#ff1f1f]"
      }`}
    >
      {children}
    </button>
  );
}

function stripHtml(value) {
  return String(value || "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}

function getBookmarkImageSrc(post) {
  const html = post?.content?.rendered || "";
  const match = String(html).match(/<img[^>]+src=["']([^"']+)["']/i);
  return match ? match[1] : "";
}

const Wishlistfavorites = () => {
  const [activeTag, setActiveTag] = useState("All");
  const [bookmarkedPosts, setBookmarkedPosts] = useState(() => readBookmarkedPosts());
  const [tourBookmarkIds, setTourBookmarkIds] = useState(() => new Set(readJsonArray(STORAGE.TOUR_BOOKMARKS, []).map(String)));

  const bookmarkedSuperDeals = useMemo(
    () => bookmarkedPosts.filter((p) => p?.__bookmarkType === "superdeals"),
    [bookmarkedPosts]
  );

  const bookmarkedBlogPosts = useMemo(
    () => bookmarkedPosts.filter((p) => p?.__bookmarkType !== "superdeals"),
    [bookmarkedPosts]
  );

  const refreshFromStorage = useCallback(() => {
    setBookmarkedPosts(readBookmarkedPosts());
    setTourBookmarkIds(new Set(readJsonArray(STORAGE.TOUR_BOOKMARKS, []).map(String)));
  }, []);

  useEffect(() => {
    refreshFromStorage();

    window.addEventListener("storage", refreshFromStorage);
    window.addEventListener("pageshow", refreshFromStorage);
    window.addEventListener("focus", refreshFromStorage);
    return () => {
      window.removeEventListener("storage", refreshFromStorage);
      window.removeEventListener("pageshow", refreshFromStorage);
      window.removeEventListener("focus", refreshFromStorage);
    };
  }, [refreshFromStorage]);

  const bookmarkedTourPackages = useMemo(() => {
    if (!tourBookmarkIds.size) return [];
    const byId = new Set(Array.from(tourBookmarkIds).map(String));
    return getAllTourBookmarkPackages().filter((pkg) => byId.has(String(pkg.id)));
  }, [tourBookmarkIds]);

  const toggleTourBookmark = useCallback((id) => {
    setTourBookmarkIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      writeJsonArray(STORAGE.TOUR_BOOKMARKS, Array.from(next));
      return next;
    });
  }, []);

  const sections = useMemo(() => {
    return [
      {
        key: "blogs",
        title: "Blog Posts",
        count: bookmarkedBlogPosts.length,
        enabled: bookmarkedBlogPosts.length > 0,
      },
      {
        key: "superdeals",
        title: "Super Deals",
        count: bookmarkedSuperDeals.length,
        enabled: bookmarkedSuperDeals.length > 0,
      },
      {
        key: "tour",
        title: "Tour Packages",
        count: bookmarkedTourPackages.length,
        enabled: bookmarkedTourPackages.length > 0,
      },
    ];
  }, [bookmarkedBlogPosts.length, bookmarkedSuperDeals.length, bookmarkedTourPackages.length]);

  const availableTags = useMemo(() => {
    const tags = [{ key: "All", label: "All" }];
    sections.forEach((s) => {
      if (!s.enabled) return;
      tags.push({ key: s.key, label: `${s.title} (${s.count})` });
    });
    return tags;
  }, [sections]);

  const visibleSections = useMemo(() => {
    if (activeTag === "All") return sections.filter((s) => s.enabled);
    return sections.filter((s) => s.enabled && s.key === activeTag);
  }, [activeTag, sections]);

  const hasWishlistItems = useMemo(() => sections.some((s) => s.enabled), [sections]);
  const [showAddMore, setShowAddMore] = useState(() => !hasWishlistItems);

  useEffect(() => {
    if (!hasWishlistItems) {
      setShowAddMore(true);
    }
  }, [hasWishlistItems]);

  const tagRows = useMemo(() => {
    const maxTopRow = 6;
    const baseTags = availableTags;
    const addMoreTag = { key: "__add_more__", label: "Add more" };
    const topSlots = Math.max(1, maxTopRow - 1);
    return {
      top: [...baseTags.slice(0, topSlots), addMoreTag],
      more: baseTags.slice(topSlots),
    };
  }, [availableTags]);

  return (
    <main className="wishlist-favorites bg-white py-8">
      <div className="mx-auto max-w-[1500px] px-[15px]">
        <div className="mt-[10px] mb-[28px]">
          <h1
            className="mb-6 text-center text-[32px] leading-[36px] sm:text-[36px] sm:leading-[36px]"
            style={{
              fontFamily: "Poppins, sans-serif",
              fontStyle: "normal",
              fontWeight: 500,
              color: "rgb(0, 0, 0)",
            }}
          >
            Wishlist
          </h1>

          <div
            className="flex flex-wrap items-center justify-center gap-3 text-center"
            style={{ fontFamily: "Poppins, sans-serif" }}
            aria-label="Wishlist filters"
          >
            {tagRows.top.map((t) => (
              <TagPill
                key={t.key}
                active={t.key === "__add_more__" ? showAddMore : activeTag === t.key}
                onClick={() => {
                  if (t.key === "__add_more__") {
                    setShowAddMore((prev) => !prev);
                    return;
                  }
                  setShowAddMore(false);
                  setActiveTag(t.key);
                }}
              >
                {t.label}
              </TagPill>
            ))}
          </div>
          {tagRows.more.length > 0 ? (
            <div className="mt-2 flex flex-wrap items-center justify-center gap-3 text-center">
              {tagRows.more.map((t) => (
                <TagPill
                  key={t.key}
                  active={activeTag === t.key}
                  onClick={() => {
                    setShowAddMore(false);
                    setActiveTag(t.key);
                  }}
                >
                  {t.label}
                </TagPill>
              ))}
            </div>
          ) : null}

          {showAddMore ? (
            <div className="mt-8">
              <CategoryPills darkMode={false} />
            </div>
          ) : null}
        </div>

        {visibleSections.length === 0 ? (
          <div className="rounded-[12px] border border-[#ef4444] bg-white px-4 py-4 text-[14px] text-[#374151]">
            No items in your wishlist yet.
          </div>
        ) : null}

        {visibleSections.some((s) => s.key === "blogs") ? (
          <section className="mb-10">
            <SectionH2 id="wishlist-blogs">Blog Posts</SectionH2>
            {bookmarkedBlogPosts.length ? (
              <div className="mt-5 grid grid-cols-1 gap-[30px] md:grid-cols-2 lg:grid-cols-3">
                {bookmarkedBlogPosts.map((post, index) => (
                  <PostCard
                    key={post.__bookmarkId || post.id || post.slug || post.link || `bm-${index}`}
                    post={post}
                    index={index}
                    showCategoryTag={true}
                    categoryLabel={post.__bookmarkCategoryLabel || "Saved"}
                    categorySlug={post.__bookmarkCategorySlug || ""}
                    categoryLink={post.__bookmarkCategoryLink || ""}
                  />
                ))}
              </div>
            ) : (
              <p className="rounded-[12px] border border-[#ef4444] bg-white px-4 py-4 text-[14px] text-[#374151]">
                No bookmarked blog posts yet.
              </p>
            )}
          </section>
        ) : null}

        {visibleSections.some((s) => s.key === "superdeals") ? (
          <section className="mb-10">
            <SectionH2 id="wishlist-superdeals">Super Deals</SectionH2>
            {bookmarkedSuperDeals.length ? (
              <div className="mt-5 grid grid-cols-1 gap-[30px] md:grid-cols-2 lg:grid-cols-3">
                {bookmarkedSuperDeals.map((post, index) => (
                  <MemoizedDealCard
                    key={post.__bookmarkId || post.id || post.slug || post.link || `sd-${index}`}
                    deal={{
                      id: post.__bookmarkId || post.id || post.slug || post.link || `sd-${index}`,
                      category: post.__bookmarkSuperDealCategory || "Super Deals",
                      title: stripHtml(post?.title?.rendered || ""),
                      displayTitle: stripHtml(post?.title?.rendered || ""),
                      description: stripHtml(post?.excerpt?.rendered || ""),
                      displayDescription: stripHtml(post?.excerpt?.rendered || ""),
                      link: post.link,
                      image: getBookmarkImageSrc(post),
                    }}
                    openPopup={() => {}}
                  />
                ))}
              </div>
            ) : (
              <p className="rounded-[12px] border border-[#ef4444] bg-white px-4 py-4 text-[14px] text-[#374151]">
                No bookmarked super deals yet.
              </p>
            )}
          </section>
        ) : null}

        {visibleSections.some((s) => s.key === "tour") ? (
          <section className="mb-10">
            <SectionH2 id="wishlist-tour">Tour Packages</SectionH2>
            {bookmarkedTourPackages.length ? (
              <div className="mt-[30px] grid grid-cols-1 items-stretch gap-[25px] md:grid-cols-2 lg:grid-cols-3">
                {bookmarkedTourPackages.map((item) => (
                  <TourMiddleCard
                    key={item.id}
                    item={item}
                    isBookmarked={tourBookmarkIds.has(String(item.id))}
                    onToggle={() => toggleTourBookmark(item.id)}
                    onShare={() => {}}
                    onForm={() => {}}
                  />
                ))}
              </div>
            ) : (
              <p className="rounded-[12px] border border-[#ef4444] bg-white px-4 py-4 text-[14px] text-[#374151]">
                No bookmarked tour packages yet.
              </p>
            )}
          </section>
        ) : null}
      </div>
    </main>
  );
};

export default Wishlistfavorites;
